import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Pencil, Trash2, Reply, X, Loader2, Send } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ReactionBar } from "@/components/social/ReactionBar";
import { timeAgo } from "@/lib/timeago";
import { MentionInput } from "@/components/social/MentionAutocomplete";
import { renderTextWithMentions } from "@/lib/mentions";
import { AuthorLink, AuthorAvatar } from "@/components/social/AuthorLink";
import { EmojiPickerButton } from "@/components/social/EmojiPickerButton";

function Composer({ onSubmit, placeholder, autoFocus, onCancel, testIdPrefix }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await onSubmit(text.trim()); setText(""); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <MentionInput multiline inputRef={inputRef} value={text} onChange={(v) => setText(v.slice(0, 1000))} maxLength={1000} rows={2} placeholder={placeholder} autoFocus={autoFocus} testId={`${testIdPrefix}-input`} />
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <EmojiPickerButton targetRef={inputRef} value={text} onChange={setText} side="top" align="start" testId={`${testIdPrefix}-emoji`} />
          <span className={`text-xs tabular-nums ${text.length > 1000 ? "font-[600] text-destructive" : "text-muted-foreground"}`} data-testid={`${testIdPrefix}-count`}>{text.length}/1,000</span>
        </div>
        <div className="flex gap-2">
          {onCancel && <Button variant="ghost" size="sm" onClick={onCancel} data-testid={`${testIdPrefix}-cancel`}>Cancel</Button>}
          <Button size="sm" onClick={submit} disabled={!text.trim() || busy} data-testid={`${testIdPrefix}-submit`}>
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Post
          </Button>
        </div>
      </div>
    </div>
  );
}

function CommentItem({ c, eventId, onChanged, isReply }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(c.body);
  const [replying, setReplying] = useState(false);
  const [saving, setSaving] = useState(false);

  const react = async (emoji) => {
    try { await api.post(`/events/comments/${c.id}/react`, { emoji }); onChanged(); }
    catch (e) { toast.error("Could not react"); }
  };
  const saveEdit = async () => {
    if (!editText.trim()) return;
    setSaving(true);
    try { await api.put(`/events/comments/${c.id}`, { body: editText.trim() }); setEditing(false); onChanged(); toast.success("Comment updated"); }
    catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    try { await api.delete(`/events/comments/${c.id}`); onChanged(); toast.success("Comment deleted"); }
    catch (e) { toast.error("Could not delete"); }
  };
  const addReply = async (body) => {
    try { await api.post(`/events/${eventId}/comments`, { body, parent_id: c.id }); setReplying(false); onChanged(); }
    catch (e) { toast.error("Could not reply"); }
  };

  const a = c.author || {};
  return (
    <div id={`comment-${c.id}`} className="flex items-start gap-3" data-testid={`event-comment-${c.id}`}>
      <AuthorAvatar card={a}>
        <Avatar className={isReply ? "h-7 w-7" : "h-9 w-9"}>
          {a.headshot_url && <AvatarImage src={fileUrl(a.headshot_url)} alt="" />}
          <AvatarFallback className="bg-teal-50 text-xs text-teal-700">{(a.display_name || "?")[0]}</AvatarFallback>
        </Avatar>
      </AuthorAvatar>
      <div className="min-w-0 flex-1">
        <div className="rounded-2xl bg-secondary/50 px-3.5 py-2.5">
          <div className="flex items-start justify-between gap-2">
            <AuthorLink card={a} className="text-sm font-[600] text-foreground" testId={`event-comment-author-${c.id}`} />
            <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(c.created_at)}{c.edited ? " · edited" : ""}</span>
          </div>
          {editing ? (
            <div className="mt-2">
              <MentionInput multiline value={editText} onChange={(v) => setEditText(v.slice(0, 1000))} maxLength={1000} rows={2} testId={`event-comment-edit-input-${c.id}`} knownHandles={(c.mentions || []).map((m) => m.handle_norm || m.handle)} />
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setEditText(c.body); }}><X className="mr-1 h-4 w-4" /> Cancel</Button>
                <Button size="sm" onClick={saveEdit} disabled={saving || !editText.trim()} data-testid={`event-comment-edit-save-${c.id}`}>{saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Save</Button>
              </div>
            </div>
          ) : (
            <p className="mt-0.5 whitespace-pre-wrap text-[14px] leading-6 text-foreground">{renderTextWithMentions(c.body, c.mentions)}</p>
          )}
        </div>

        {!editing && (
          <div className="mt-1.5 flex flex-wrap items-center gap-3 pl-1">
            <ReactionBar reactions={c.reactions} myReaction={c.my_reaction} onReact={react} testId={`event-comment-react-${c.id}`} />
            {!isReply && (
              <button onClick={() => setReplying((r) => !r)} className="inline-flex items-center gap-1 text-xs font-[540] text-muted-foreground hover:text-primary" data-testid={`event-comment-reply-${c.id}`}>
                <Reply className="h-3.5 w-3.5" /> Reply
              </button>
            )}
            {c.can_edit && (
              <>
                <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-xs font-[540] text-muted-foreground hover:text-primary" data-testid={`event-comment-edit-${c.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="inline-flex items-center gap-1 text-xs font-[540] text-muted-foreground hover:text-destructive" data-testid={`event-comment-delete-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete this comment?</AlertDialogTitle><AlertDialogDescription>This can&apos;t be undone.{!isReply ? " Replies will be removed too." : ""}</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        )}

        {replying && (
          <div className="mt-2.5">
            <Composer onSubmit={addReply} placeholder="Write a reply…" autoFocus onCancel={() => setReplying(false)} testIdPrefix={`event-reply-${c.id}`} />
          </div>
        )}
      </div>
    </div>
  );
}

export function EventComments({ eventId }) {
  const [items, setItems] = useState(null);
  const [params] = useSearchParams();
  const targetComment = params.get("comment");

  const load = async () => {
    try { const { data } = await api.get(`/events/${eventId}/comments`); setItems(data); }
    catch (e) { setItems([]); }
  };
  useEffect(() => {
    let active = true;
    (async () => {
      try { const { data } = await api.get(`/events/${eventId}/comments`); if (active) setItems(data); }
      catch (e) { if (active) setItems([]); }
    })();
    return () => { active = false; };
  }, [eventId]);

  useEffect(() => {
    if (!targetComment || !items) return undefined;
    const found = items.some((c) => c.id === targetComment || (c.replies || []).some((r) => r.id === targetComment));
    if (!found) return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById(`comment-${targetComment}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("so-flash");
        setTimeout(() => el.classList.remove("so-flash"), 2900);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [targetComment, items]);

  const add = async (body) => {
    try { await api.post(`/events/${eventId}/comments`, { body }); load(); }
    catch (e) { toast.error("Could not post comment"); }
  };

  const count = (items || []).reduce((n, c) => n + 1 + (c.replies || []).length, 0);

  return (
    <Card className="border-0 p-6" data-testid="event-comments">
      <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground">
        <MessageSquare className="h-5 w-5 text-primary" /> Comments {count > 0 && <span className="text-muted-foreground">({count})</span>}
      </h2>
      <div className="mt-4"><Composer onSubmit={add} placeholder="Share your thoughts about this event…" testIdPrefix="event-comment" /></div>
      <div className="mt-6 space-y-5">
        {items === null ? null : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Be the first to comment.</p>
        ) : (
          items.map((c) => (
            <div key={c.id}>
              <CommentItem c={c} eventId={eventId} onChanged={load} />
              {(c.replies || []).length > 0 && (
                <div className="ml-12 mt-3 space-y-3 border-l-2 border-border pl-3">
                  {c.replies.map((r) => <CommentItem key={r.id} c={r} eventId={eventId} onChanged={load} isReply />)}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </Card>
  );
}
